import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button, Input, Label, Textarea } from "@lumanity/ui/primitives";
import ExampleDocuments from "./ExampleDocuments";
import KeyMessagesInput from "./KeyMessagesInput";
import { SAMPLE_BRIEF, cleanBrief, normalizeBrief, sameJson, startingBrief } from "./model";
import type { BriefStepProps } from "./briefSteps";
import type { Brief } from "./types";

// Shown again if the writer clears a field.
const PLACEHOLDERS = {
  objective: SAMPLE_BRIEF.objective,
  audience: SAMPLE_BRIEF.audience,
  keyMessages: SAMPLE_BRIEF.key_messages,
  mandatory: SAMPLE_BRIEF.mandatory_content,
  exclusions: SAMPLE_BRIEF.exclusions,
  instructions: SAMPLE_BRIEF.author_client_instructions,
  evidence: SAMPLE_BRIEF.must_use_evidence,
};

/** Key messages always show at least one (empty) row to type into. */
const toFormBrief = (brief: Brief): Brief => {
  const b = normalizeBrief(brief);
  return b.key_messages.length ? b : { ...b, key_messages: [""] };
};

/** Brief step 2 (FR-4-06..): the writer's strategic intent and constraints. Every field is optional. */
const GeneralGuidanceStep = ({ output, save, saving, onDirtyChange, onContinue }: BriefStepProps) => {
  const [saved] = useState(() => toFormBrief(output.brief));
  // A Brief nobody has written in starts from the sample text, which is saved with the first Save / Continue.
  const [start] = useState(() => toFormBrief(startingBrief(normalizeBrief(output.brief))));
  const [brief, setBrief] = useState(start);

  const dirty = !sameJson(cleanBrief(brief), cleanBrief(saved));
  // Warn about leaving only once the writer has changed something themselves.
  const edited = !sameJson(cleanBrief(brief), cleanBrief(start));
  useEffect(() => onDirtyChange(edited), [edited, onDirtyChange]);

  const set = <K extends keyof Brief>(key: K, value: Brief[K]) => setBrief((b) => ({ ...b, [key]: value }));

  const handleSave = () => void save({ brief: cleanBrief(brief) }, { toastOnSuccess: true });
  const handleContinue = async () => {
    if (dirty && !(await save({ brief: cleanBrief(brief) }))) return;
    onContinue();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-brand-navy">General writer instructions</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Capture the strategic intent and constraints before outlining. All fields optional — improves downstream outline quality
        </p>
      </div>

      <Field label="Publication Objective" htmlFor="brief-objective">
        <Textarea
          id="brief-objective"
          rows={3}
          value={brief.objective}
          placeholder={PLACEHOLDERS.objective}
          onChange={(e) => set("objective", e.target.value)}
          disabled={saving}
        />
      </Field>

      <Field label="Target Audience" htmlFor="brief-audience">
        <Input
          id="brief-audience"
          value={brief.audience}
          placeholder={PLACEHOLDERS.audience}
          onChange={(e) => set("audience", e.target.value)}
          disabled={saving}
        />
      </Field>

      <Field label="Key Messages" htmlFor="brief-key-messages" hint="In order of importance">
        <KeyMessagesInput
          id="brief-key-messages"
          value={brief.key_messages}
          onChange={(v) => set("key_messages", v)}
          placeholders={PLACEHOLDERS.keyMessages}
          disabled={saving}
        />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Mandatory Content" htmlFor="brief-mandatory">
          <Textarea
            id="brief-mandatory"
            rows={4}
            value={brief.mandatory_content}
            placeholder={PLACEHOLDERS.mandatory}
            onChange={(e) => set("mandatory_content", e.target.value)}
            disabled={saving}
          />
        </Field>
        <Field label="Exclusions" htmlFor="brief-exclusions">
          <Textarea
            id="brief-exclusions"
            rows={4}
            value={brief.exclusions}
            placeholder={PLACEHOLDERS.exclusions}
            onChange={(e) => set("exclusions", e.target.value)}
            disabled={saving}
          />
        </Field>
      </div>

      <Field label="Author / Client Instructions" htmlFor="brief-instructions">
        <Textarea
          id="brief-instructions"
          rows={3}
          value={brief.author_client_instructions}
          placeholder={PLACEHOLDERS.instructions}
          onChange={(e) => set("author_client_instructions", e.target.value)}
          disabled={saving}
        />
      </Field>

      <Field label="Must-Use Evidence" htmlFor="brief-evidence">
        <Textarea
          id="brief-evidence"
          rows={3}
          value={brief.must_use_evidence}
          placeholder={PLACEHOLDERS.evidence}
          onChange={(e) => set("must_use_evidence", e.target.value)}
          disabled={saving}
        />
      </Field>

      <div className="space-y-1.5">
        <p className="text-sm font-semibold text-brand-navy">Examples / Templates</p>
        <ExampleDocuments
          projectId={output.project_id}
          ids={brief.example_document_ids}
          onAdd={(id) => setBrief((b) => ({ ...b, example_document_ids: [...b.example_document_ids, id] }))}
          onRemove={(id) => setBrief((b) => ({ ...b, example_document_ids: b.example_document_ids.filter((x) => x !== id) }))}
          disabled={saving}
        />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-5">
        <Button type="button" variant="outline" onClick={handleSave} disabled={saving || !dirty}>
          Save Draft
        </Button>
        <Button type="button" className="bg-brand-teal hover:bg-brand-teal/90 text-white" onClick={() => void handleContinue()} disabled={saving}>
          Continue to Outline <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

const Field = ({ label, htmlFor, hint, children }: { label: string; htmlFor: string; hint?: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor} className="font-semibold text-brand-navy">
      {label}
      {hint && <span className="ml-1.5 font-normal text-muted-foreground">({hint})</span>}
    </Label>
    {children}
  </div>
);

export default GeneralGuidanceStep;
